import React, { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertCircle, TrendingUp, MessageSquare, Users, Clock, Star, Download, RefreshCw } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { API_URL } from '../config/api';

interface ChatbotAnalyticsProps {}

interface OverviewData {
  overview: {
    totalConversations: number;
    totalMessages: number;
    uniqueUsers: number;
    avgDuration: number;
    avgSatisfaction: number;
    resolutionRate: number;
    avgQualityScore: number;
  };
  dailyTrends: Array<{
    date: string;
    conversations: number;
    messages: number;
    avg_duration: number;
    resolution_rate: number;
  }>;
  topIntents: Array<{
    detected_intent: string;
    frequency: number;
    avg_confidence: number;
  }>;
  contentGaps: Array<{
    question_text: string;
    question_intent: string;
    frequency_count: number;
    avg_quality_score: number;
    status: string;
  }>;
}

interface ConversationCountData {
  overview: {
    total_conversations: number;
    total_user_messages: number;
    total_assistant_messages: number;
    daily: number;
    weekly: number;
    monthly: number;
    allTime: number;
  };
  dailyTrends: Array<{
    date: string;
    conversations: number;
    user_messages: number;
    assistant_messages: number;
  }>;
}

interface IntentData {
  intents: Array<{
    detected_intent: string;
    intent_category: string;
    frequency: number;
    avg_confidence: number;
    avg_quality_score: number;
    sample_messages: string[];
  }>;
  categories: Array<{
    intent_category: string;
    frequency: number;
    avg_confidence: number;
  }>;
  trends: Array<{
    date: string;
    detected_intent: string;
    frequency: number;
  }>;
}

interface QualityData {
  overview: {
    avgRelevance: number;
    avgHelpfulness: number;
    avgAccuracy: number;
    avgCompleteness: number;
    avgOverall: number;
    totalScoredResponses: number;
    lowQualityResponses: number;
  };
  qualityTrends: Array<{
    date: string;
    avg_quality: number;
    responses_count: number;
    low_quality_count: number;
  }>;
  lowQualityResponses: Array<{
    user_message: string;
    bot_response: string;
    overall_score: number;
    scoring_reasoning: string;
    created_at: string;
    detected_intent: string;
  }>;
}

interface FlowData {
  flows: Array<{
    flow_pattern: string;
    frequency_count: number;
    avg_duration_seconds: number;
    completion_rate: number;
    conversion_rate: number;
    abandonment_points: string[];
  }>;
  lengthDistribution: Array<{
    length_category: string;
    conversation_count: number;
    avg_duration: number;
  }>;
  abandonmentAnalysis: Array<{
    primary_intent: string;
    total_conversations: number;
    abandoned_conversations: number;
    abandonment_rate: number;
    avg_messages_before_abandonment: number;
  }>;
}

interface ContentGapData {
  contentGaps: Array<{
    id: number;
    question_text: string;
    question_intent: string;
    frequency_count: number;
    avg_quality_score: number;
    first_seen: string;
    last_seen: string;
    status: string;
    suggested_content: string;
    related_pages: string[];
  }>;
  gapTrends: Array<{
    date: string;
    new_gaps: number;
    avg_quality: number;
  }>;
  gapCategories: Array<{
    question_intent: string;
    gap_count: number;
    avg_frequency: number;
    avg_quality: number;
  }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const ChatbotAnalytics: React.FC<ChatbotAnalyticsProps> = () => {
  const { selectedSite } = useSite();
  const siteId = selectedSite?._id || selectedSite?.id;
  const [timeRange, setTimeRange] = useState('90'); // Default to 90 days to capture older data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  // Toggle between count and percentage display
  const [categoryDisplayMode, setCategoryDisplayMode] = useState<'count' | 'percentage'>('count');
  
  // Debug log to verify component is updated
  console.log('ChatbotAnalytics MAIN COMPONENT LOADED v3: categoryDisplayMode =', categoryDisplayMode);
  
  // Add visible debug indicator
  React.useEffect(() => {
    console.log('ChatbotAnalytics mounted - adding debug indicator');
    const debugDiv = document.createElement('div');
    debugDiv.id = 'chatbot-analytics-debug';
    debugDiv.style.cssText = 'position: fixed; top: 10px; right: 10px; background: red; color: white; padding: 10px; z-index: 9999; font-weight: bold;';
    debugDiv.textContent = 'ChatbotAnalytics v3 LOADED';
    document.body.appendChild(debugDiv);
    
    return () => {
      const el = document.getElementById('chatbot-analytics-debug');
      if (el) el.remove();
    };
  }, []);
  
  // Data states
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [conversationCountData, setConversationCountData] = useState<ConversationCountData | null>(null);
  const [intentData, setIntentData] = useState<IntentData | null>(null);
  const [qualityData, setQualityData] = useState<QualityData | null>(null);
  const [flowData, setFlowData] = useState<FlowData | null>(null);
  const [contentGapData, setContentGapData] = useState<ContentGapData | null>(null);

  useEffect(() => {
    if (siteId) {
      loadData();
    }
  }, [siteId, timeRange]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadOverviewData(),
        loadConversationCountData(),
        loadIntentData(),
        loadQualityData(),
        loadFlowData(),
        loadContentGapData()
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadOverviewData = async () => {
    const authToken = localStorage.getItem('auth_token') || 'test-token';
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/overview?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load overview data');
    const data = await response.json();
    setOverviewData(data);
  };

  const loadConversationCountData = async () => {
    const authToken = localStorage.getItem('auth_token') || 'test-token';
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/conversation-counts?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load conversation count data');
    const data = await response.json();
    setConversationCountData(data);
  };

  const loadIntentData = async () => {
    try {
      const authToken = localStorage.getItem('auth_token') || 'test-token';
      const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/intents?days=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to load intent data: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      setIntentData(data);
    } catch (error) {
      console.error('Error loading intent data:', error);
      // Don't mask the error - let it propagate
      throw error;
    }
  };

  const loadQualityData = async () => {
    const authToken = localStorage.getItem('auth_token') || 'test-token';
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/quality?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load quality data');
    const data = await response.json();
    setQualityData(data);
  };

  const loadFlowData = async () => {
    const authToken = localStorage.getItem('auth_token') || 'test-token';
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/flows?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load flow data');
    const data = await response.json();
    setFlowData(data);
  };

  const loadContentGapData = async () => {
    const authToken = localStorage.getItem('auth_token') || 'test-token';
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/content-gaps?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load content gap data');
    const data = await response.json();
    setContentGapData(data);
  };

  const exportData = async (type: string, format: string = 'json') => {
    try {
      const authToken = localStorage.getItem('auth_token') || 'test-token';
      const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/export?type=${type}&format=${format}&days=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `chatbot-analytics-${type}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Export failed');
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <AlertCircle className="h-8 w-8 text-red-500" />
        <span className="ml-2 text-red-500">{error}</span>
        <Button onClick={loadData} variant="outline" className="ml-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Chatbot Analytics</h1>
          <p className="text-gray-600">Comprehensive analysis of chatbot conversations and performance</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
              <SelectItem value="9999">All time</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={() => exportData('overview')} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          
          <Button onClick={loadData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="intents">Intent Analysis</TabsTrigger>
          <TabsTrigger value="quality">Quality Scoring</TabsTrigger>
          <TabsTrigger value="flows">Conversation Flows</TabsTrigger>
          <TabsTrigger value="gaps">Content Gaps</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {overviewData && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{conversationCountData?.overview?.total_conversations?.toLocaleString() || overviewData.overview.totalConversations.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">
                      {conversationCountData?.overview?.total_user_messages?.toLocaleString() || overviewData.overview.totalMessages.toLocaleString()} user messages
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Unique Users</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{overviewData.overview.uniqueUsers.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">
                      {(overviewData.overview.totalMessages / overviewData.overview.uniqueUsers).toFixed(1)} messages per user
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatDuration(overviewData.overview.avgDuration)}</div>
                    <p className="text-xs text-muted-foreground">
                      {formatPercentage(overviewData.overview.resolutionRate)} resolution rate
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Quality Score</CardTitle>
                    <Star className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(overviewData.overview.avgQualityScore * 100).toFixed(1)}%</div>
                    <p className="text-xs text-muted-foreground">
                      {(overviewData.overview.avgSatisfaction * 100).toFixed(1)}% satisfaction
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Trends Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Conversation Trends</CardTitle>
                  <CardDescription>Number of conversations per day</CardDescription>
                </CardHeader>
                <CardContent>
                  {conversationCountData?.dailyTrends && conversationCountData.dailyTrends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={conversationCountData.dailyTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis />
                        <Tooltip 
                          labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="conversations" 
                          stroke="#8884d8" 
                          name="Conversations" 
                          strokeWidth={2}
                          dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                      <div className="text-4xl mb-4">📈</div>
                      <p className="text-lg font-medium">No conversation data available</p>
                      <p className="text-sm mt-2">Conversations will appear here once the chatbot is active</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Intents and Content Gaps */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Top User Intents</CardTitle>
                    <CardDescription>Most common user intentions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {overviewData.topIntents.slice(0, 5).map((intent, index) => (
                        <div key={intent.detected_intent} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                            <span className="text-sm font-medium">{intent.detected_intent}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold">{intent.frequency}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatPercentage(intent.avg_confidence)} confidence
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Content Gaps</CardTitle>
                    <CardDescription>Questions with poor response quality</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {overviewData.contentGaps.slice(0, 5).map((gap, index) => (
                        <div key={index} className="border-l-4 border-red-500 pl-4">
                          <div className="text-sm font-medium truncate">{gap.question_text}</div>
                          <div className="flex items-center justify-between mt-1">
                            <Badge variant={gap.status === 'identified' ? 'destructive' : 'secondary'}>
                              {gap.status}
                            </Badge>
                            <div className="text-xs text-muted-foreground">
                              {gap.frequency_count} occurrences · {(gap.avg_quality_score * 100).toFixed(1)}% quality
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="intents" className="space-y-6">
          {intentData && (
            <>
              {/* Intent Categories */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Intent Categories Distribution TEST v2</CardTitle>
                      <CardDescription>Breakdown of user intentions by category</CardDescription>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-100 p-2 rounded">
                      <Button
                        variant={categoryDisplayMode === 'count' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          console.log('Switching to count mode');
                          setCategoryDisplayMode('count');
                        }}
                        className="h-8"
                      >
                        Count
                      </Button>
                      <Button
                        variant={categoryDisplayMode === 'percentage' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          console.log('Switching to percentage mode');
                          setCategoryDisplayMode('percentage');
                        }}
                        className="h-8"
                      >
                        Percentage
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {intentData.categories && intentData.categories.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={intentData.categories}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ intent_category, frequency, percent }) => {
                            // Format intent category names for better display
                            const formattedCategory = intent_category
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, l => l.toUpperCase());
                            const displayValue = categoryDisplayMode === 'percentage' 
                              ? `${(percent * 100).toFixed(1)}%`
                              : frequency;
                            return `${formattedCategory}: ${displayValue}`;
                          }}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="frequency"
                        >
                          {intentData.categories.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => {
                            const formattedName = name.toString()
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, l => l.toUpperCase());
                            if (categoryDisplayMode === 'percentage') {
                              const total = intentData.categories.reduce((sum, cat) => sum + cat.frequency, 0);
                              const percentage = ((value as number) / total * 100).toFixed(1);
                              return [`${percentage}%`, formattedName];
                            }
                            return [value, formattedName];
                          }}
                        />
                        <Legend 
                          formatter={(value) => {
                            return value.toString()
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, l => l.toUpperCase());
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                      <div className="text-4xl mb-4">📊</div>
                      <p className="text-lg font-medium">No intent category data available</p>
                      <p className="text-sm mt-2">Intent categories will appear once conversations are analyzed</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Detailed Intent Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle>Detailed Intent Analysis</CardTitle>
                  <CardDescription>Intent frequency with quality scores and sample messages</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {intentData.intents.slice(0, 10).map((intent, index) => (
                      <div key={intent.detected_intent} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold">{intent.detected_intent}</h4>
                            <Badge variant="secondary">{intent.intent_category}</Badge>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold">{intent.frequency} occurrences</div>
                            <div className="text-xs text-muted-foreground">
                              {formatPercentage(intent.avg_confidence)} confidence · {(intent.avg_quality_score * 100).toFixed(1)}% quality
                            </div>
                          </div>
                        </div>
                        
                        {intent.sample_messages && intent.sample_messages.length > 0 && (
                          <div className="mt-3">
                            <h5 className="text-sm font-medium text-muted-foreground mb-2">Sample Messages:</h5>
                            <div className="space-y-1">
                              {intent.sample_messages.slice(0, 3).map((message, msgIndex) => (
                                <div key={msgIndex} className="text-sm bg-gray-50 rounded p-2 truncate">
                                  "{message}"
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="quality" className="space-y-6">
          {qualityData && (
            <>
              {/* Quality Metrics Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Relevance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgRelevance * 100).toFixed(1)}%</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Helpfulness</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgHelpfulness * 100).toFixed(1)}%</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Accuracy</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgAccuracy * 100).toFixed(1)}%</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Completeness</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgCompleteness * 100).toFixed(1)}%</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Overall</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgOverall * 100).toFixed(1)}%</div>
                    <p className="text-xs text-muted-foreground">
                      {qualityData.overview.lowQualityResponses} low quality
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Quality Trends */}
              <Card>
                <CardHeader>
                  <CardTitle>Quality Trends</CardTitle>
                  <CardDescription>Response quality over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={qualityData.qualityTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="avg_quality" stackId="1" stroke="#8884d8" fill="#8884d8" name="Avg Quality" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Low Quality Responses */}
              <Card>
                <CardHeader>
                  <CardTitle>Low Quality Responses</CardTitle>
                  <CardDescription>Responses that need improvement</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {qualityData.lowQualityResponses.slice(0, 5).map((response, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-red-50 border-red-200">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="destructive">Quality Score: {(response.overall_score * 100).toFixed(1)}%</Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(response.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <strong className="text-sm">User:</strong>
                            <p className="text-sm mt-1">{response.user_message}</p>
                          </div>
                          <div>
                            <strong className="text-sm">Bot:</strong>
                            <p className="text-sm mt-1">{response.bot_response}</p>
                          </div>
                          
                          {response.scoring_reasoning && (
                            <div>
                              <strong className="text-sm">Analysis:</strong>
                              <p className="text-xs text-muted-foreground mt-1">{response.scoring_reasoning}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="flows" className="space-y-6">
          {flowData && (
            <>
              {/* Conversation Length Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Conversation Length Distribution</CardTitle>
                  <CardDescription>How long conversations typically last</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={flowData.lengthDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="length_category" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="conversation_count" fill="#8884d8" name="Conversations" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Top Conversation Flows */}
              <Card>
                <CardHeader>
                  <CardTitle>Most Common Conversation Flows</CardTitle>
                  <CardDescription>Intent sequences and completion rates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {flowData.flows.slice(0, 10).map((flow, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded">{flow.flow_pattern}</code>
                          <Badge variant="secondary">{flow.frequency_count} times</Badge>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Duration:</span>
                            <div className="font-medium">{formatDuration(flow.avg_duration_seconds)}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Completion:</span>
                            <div className="font-medium">{formatPercentage(flow.completion_rate)}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Conversion:</span>
                            <div className="font-medium">{formatPercentage(flow.conversion_rate)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Abandonment Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle>Abandonment Analysis</CardTitle>
                  <CardDescription>Where users typically drop off</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {flowData.abandonmentAnalysis.slice(0, 10).map((analysis, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold">{analysis.primary_intent}</h4>
                          <Badge variant={analysis.abandonment_rate > 0.5 ? 'destructive' : 'secondary'}>
                            {formatPercentage(analysis.abandonment_rate)} abandonment
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Total Conversations:</span>
                            <div className="font-medium">{analysis.total_conversations}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Avg Messages Before Abandonment:</span>
                            <div className="font-medium">{analysis.avg_messages_before_abandonment ? Number(analysis.avg_messages_before_abandonment).toFixed(1) : 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="gaps" className="space-y-6">
          {contentGapData && (
            <>
              {/* Gap Categories */}
              <Card>
                <CardHeader>
                  <CardTitle>Content Gap Categories</CardTitle>
                  <CardDescription>Types of questions that need better answers</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {contentGapData.gapCategories.map((category, index) => (
                      <div key={category.question_intent} className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-2">
                          {category.question_intent
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, l => l.toUpperCase())}
                        </h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Gaps:</span>
                            <span className="font-medium">{category.gap_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Avg Frequency:</span>
                            <span className="font-medium">{Number(category.avg_frequency).toFixed(1)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Avg Quality:</span>
                            <span className="font-medium">{(Number(category.avg_quality) * 100).toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Content Gaps */}
              <Card>
                <CardHeader>
                  <CardTitle>Identified Content Gaps</CardTitle>
                  <CardDescription>Questions that need attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {contentGapData.contentGaps.slice(0, 10).map((gap) => (
                      <div key={gap.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant={
                            gap.status === 'identified' ? 'destructive' :
                            gap.status === 'addressed' ? 'default' : 'secondary'
                          }>
                            {gap.status}
                          </Badge>
                          <div className="text-sm text-muted-foreground">
                            {gap.frequency_count} occurrences · {(gap.avg_quality_score * 100).toFixed(1)}% quality
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <strong className="text-sm">Question:</strong>
                            <p className="text-sm mt-1">{gap.question_text}</p>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                            <span>Intent: {gap.question_intent
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, l => l.toUpperCase())}</span>
                            <span>First seen: {new Date(gap.first_seen).toLocaleDateString()}</span>
                            <span>Last seen: {new Date(gap.last_seen).toLocaleDateString()}</span>
                          </div>
                          
                          {gap.suggested_content && (
                            <div>
                              <strong className="text-sm">Suggestion:</strong>
                              <p className="text-sm mt-1 text-muted-foreground">{gap.suggested_content}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Export with toggle functionality
export default ChatbotAnalytics;